import type {
    CreatorSubscriptionProvider,
    ICreatorSubscription,
    IUpdateCreatorSubscriptionPaymentResult,
} from '../types/global/creatorSubscription.types';
import type {
    IPremiumCampaignImpact,
    IPremiumCampaignImpactCampaign,
    ISubscriptionPlan,
} from '../redux/subscription/types';
import { normalizeCreatorPlanClass, resolveCreatorEntitlements } from './planEntitlements';
import type { CreatorEntitlements } from './planEntitlements';

const MANAGEABLE_STATUSES = new Set(['active', 'trialing', 'non-renewing', 'attention', 'past_due']);
const ACTIVE_BENEFIT_STATUSES = new Set(['active', 'trialing', 'non-renewing']);

export function getCreatorSubscriptionProvider(
    sub:
        | {
              stripe_customer_code?: string | null;
              subscription_id?: string | null;
              subscription_code?: string | null;
              source?: string | null;
              payment_provider?: string | null;
              authorization?: { flutterwave_token?: string | null; token?: string | null };
              subscription_details?: {
                  source?: string | null;
                  payment_source?: string | null;
                  flutterwave_plan_id?: string | null;
                  flutterwave_card_token?: string | null;
              } | null;
          }
        | null
        | undefined,
): CreatorSubscriptionProvider {
    if (!sub) return null;

    const details = sub.subscription_details;
    const explicit = (sub.payment_provider ?? sub.source ?? details?.payment_source ?? '').toLowerCase();
    if (explicit === 'flutterwave') return 'flutterwave';
    if (explicit === 'paystack') return 'paystack';
    if (explicit === 'stripe') return 'stripe';

    const code = String(sub.subscription_code || '');
    if (code.startsWith('FLW_')) return 'flutterwave';
    if (details?.flutterwave_plan_id || details?.flutterwave_card_token || sub.authorization?.flutterwave_token) {
        return 'flutterwave';
    }

    // Paystack before Stripe — documents may retain stale stripe_customer_code after switching rails.
    if (code) return 'paystack';
    if (sub.stripe_customer_code && sub.subscription_id) return 'stripe';
    return null;
}

/** Prefer live provider status unless the document is in a terminal state (avoid stale Stripe cache). */
export function resolveEffectiveCreatorSubscriptionStatus(row: ICreatorSubscription): string {
    const docStatus = (row.status ?? '').toLowerCase();
    const providerStatus = row.subscription_details?.status?.toLowerCase();

    if (docStatus === 'canceled' || docStatus === 'cancelled' || docStatus === 'complete') {
        return docStatus === 'cancelled' ? 'canceled' : docStatus;
    }

    if (providerStatus === 'active' || providerStatus === 'trialing') {
        return providerStatus;
    }
    if (providerStatus === 'past_due') return 'past_due';
    if (providerStatus === 'canceled' || providerStatus === 'cancelled') {
        return 'canceled';
    }

    if (docStatus && MANAGEABLE_STATUSES.has(docStatus)) return docStatus;

    return row.status ?? '';
}

export function isTerminalCreatorSubscriptionStatus(status?: string | null): boolean {
    const normalized = (status ?? '').toLowerCase();
    return normalized === 'canceled' || normalized === 'cancelled' || normalized === 'complete';
}

export function isManageableCreatorSubscriptionStatus(status?: string | null): boolean {
    return Boolean(status && MANAGEABLE_STATUSES.has(status));
}

export function hasActiveCreatorSubscriptionBenefits(
    sub: ICreatorSubscription | ISubscriptionPlan | null | undefined,
): boolean {
    if (!sub) return false;
    const planName = sub.plan_name ?? (sub as ISubscriptionPlan).name ?? '';
    if (!planName || planName === 'Free') return false;
    return ACTIVE_BENEFIT_STATUSES.has(sub.status ?? '');
}

export function extractCreatorSubscriptionsFromGetMyPlanPayload(payload: unknown): ICreatorSubscription[] {
    if (!payload || typeof payload !== 'object') return [];

    const root = payload as Record<string, unknown>;

    if (Array.isArray(root)) {
        return root as ICreatorSubscription[];
    }

    const dataLayer = root.data;

    if (Array.isArray(dataLayer)) {
        return dataLayer as ICreatorSubscription[];
    }

    if (dataLayer && typeof dataLayer === 'object') {
        const nested = (dataLayer as Record<string, unknown>).data;
        if (Array.isArray(nested)) return nested as ICreatorSubscription[];
    }

    return [];
}

function rowRecencyMs(row: ICreatorSubscription): number {
    const iso = row.updatedAt ?? row.createdAt ?? row.created_at ?? row.start_date;
    if (!iso) return 0;
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : 0;
}

function subscriptionRowPriority(row: ICreatorSubscription): number {
    const status = resolveEffectiveCreatorSubscriptionStatus(row);
    if (status === 'active') return 0;
    if (status === 'trialing') return 1;
    if (isManageableCreatorSubscriptionStatus(status)) return 2;
    if (row.paid && row.plan_name) return 3;
    return 4;
}

export function pickRecentCanceledCreatorSubscription(rows: ICreatorSubscription[]): ICreatorSubscription | null {
    const canceled = rows.filter(row => {
        const status = resolveEffectiveCreatorSubscriptionStatus(row);
        if (!isTerminalCreatorSubscriptionStatus(status)) return false;
        const name = row.plan_name ?? row.plan?.name ?? '';
        if (!name || name === 'Free') return false;
        return true;
    });
    if (!canceled.length) return null;
    const sorted = [...canceled].sort((a, b) => rowRecencyMs(b) - rowRecencyMs(a));
    return sorted[0] ?? null;
}

export function pickPrimaryCreatorSubscription(rows: ICreatorSubscription[]): ICreatorSubscription | null {
    if (!rows.length) return null;

    const eligible = rows.filter(row => {
        const status = resolveEffectiveCreatorSubscriptionStatus(row);
        return !isTerminalCreatorSubscriptionStatus(status);
    });

    if (eligible.length) {
        const sorted = [...eligible].sort((a, b) => {
            const priorityDiff = subscriptionRowPriority(a) - subscriptionRowPriority(b);
            if (priorityDiff !== 0) return priorityDiff;
            return rowRecencyMs(b) - rowRecencyMs(a);
        });
        return sorted[0] ?? null;
    }

    // No live plan — keep the latest canceled paid plan so the UI can show a post-cancel strip.
    return pickRecentCanceledCreatorSubscription(rows);
}

export function parseGetMyPlanPrimarySubscription(payload: unknown): ISubscriptionPlan | undefined {
    const rows = extractCreatorSubscriptionsFromGetMyPlanPayload(payload);
    const primary = pickPrimaryCreatorSubscription(rows);
    if (!primary) return undefined;
    const normalized = normalizeCreatorSubscriptionRow(primary);
    const impact = extractPremiumCampaignImpact(payload);
    if (impact) {
        normalized.premium_campaign_impact = impact;
    }
    return normalized;
}

export function extractPremiumCampaignImpact(payload: unknown): IPremiumCampaignImpact | undefined {
    if (!payload || typeof payload !== 'object') return undefined;
    const root = payload as Record<string, unknown>;
    const raw = root.premium_campaign_impact;
    if (!raw || typeof raw !== 'object') return undefined;
    const impact = raw as Record<string, unknown>;
    const campaignsRaw = Array.isArray(impact.campaigns) ? impact.campaigns : [];
    const campaigns = campaignsRaw
        .filter((c): c is Record<string, unknown> => Boolean(c) && typeof c === 'object')
        .map(c => ({
            participation_id: String(c.participation_id ?? ''),
            campaign_id: String(c.campaign_id ?? ''),
            title: String(c.title ?? 'Premium campaign'),
            status: String(c.status ?? ''),
            premium_requirements: Array.isArray(c.premium_requirements) ? c.premium_requirements.map(String) : [],
        }));
    return {
        has_approved_premium_campaigns: Boolean(impact.has_approved_premium_campaigns ?? campaigns.length > 0),
        campaigns,
    };
}

/** Campaigns the selected lower plan cannot continue supporting. */
export function filterPremiumCampaignsLostOnPlan(
    campaigns: IPremiumCampaignImpactCampaign[] | undefined,
    planEntitlements: CreatorEntitlements | null | undefined,
): IPremiumCampaignImpactCampaign[] {
    if (!campaigns?.length) return [];
    if (!planEntitlements) return campaigns;
    return campaigns.filter(c => {
        const required = c.premium_requirements || [];
        if (!required.length) return true;
        return required.some(key => !(planEntitlements as Record<string, boolean>)[key]);
    });
}

function inferSource(provider: CreatorSubscriptionProvider): string {
    if (provider === 'paystack') return 'paystack';
    if (provider === 'flutterwave') return 'flutterwave';
    if (provider === 'stripe') return 'stripe';
    return '';
}

function resolvePlanCodeFromRow(row: ICreatorSubscription): string {
    return (
        row.plan?.plan_code ??
        row.subscription_plan ??
        (row.plan?.id != null ? String(row.plan.id) : '') ??
        row.plan_code ??
        ''
    );
}

export function normalizeCreatorSubscriptionRow(row: ICreatorSubscription): ISubscriptionPlan {
    const provider = getCreatorSubscriptionProvider(row);
    const effectiveStatus = resolveEffectiveCreatorSubscriptionStatus(row);
    const planCode = resolvePlanCodeFromRow(row);
    const stripePlan = row.subscription_details?.plan;
    const createdAt = row.createdAt ?? row.created_at ?? row.start_date ?? '';
    const periodEnd = row.current_period_end;
    const nextPayment = row.next_payment_date ?? (typeof periodEnd === 'string' ? periodEnd : undefined);

    return {
        _id: row._id,
        name: row.plan_name ?? row.plan?.name ?? row.name ?? '',
        plan_name: row.plan_name ?? row.plan?.name ?? row.name ?? undefined,
        amount: row.amount ?? stripePlan?.amount ?? row.plan?.amount ?? 0,
        interval: (stripePlan?.interval ??
            row.plan?.interval ??
            row.interval ??
            'month') as ISubscriptionPlan['interval'],
        subscription_plan: row.subscription_plan ?? planCode,
        description: row.description ?? '',
        features: row.features ?? [],
        plan_code: planCode,
        limit: 0,
        currency: row.currency ?? '',
        created_at: createdAt,
        created_by: '',
        source: row.source ?? row.payment_provider ?? inferSource(provider),
        payment_provider: (row.payment_provider ?? inferSource(provider)) || undefined,
        plan: {
            id: row.plan?.id ?? planCode,
            name: row.plan?.name ?? row.plan_name,
            plan_code: planCode,
            amount: stripePlan?.amount ?? row.plan?.amount ?? row.amount ?? 0,
            interval: stripePlan?.interval ?? row.plan?.interval ?? row.interval ?? 'month',
        },
        preferred_plan: false,
        active: row.active ?? effectiveStatus === 'active',
        country: '',
        region: '',
        original_price: row.amount ?? stripePlan?.amount ?? 0,
        converted_amount: '',
        transaction_currency: '',
        converted_amount_no_symbol: '',
        subscription_code: row.subscription_code ?? undefined,
        subscription_id: row.subscription_id ?? undefined,
        stripe_customer_code: row.stripe_customer_code ?? undefined,
        paystack_customer_code: row.paystack_customer_code ?? undefined,
        next_payment_date: nextPayment ?? undefined,
        current_period_end: typeof periodEnd === 'string' ? periodEnd : undefined,
        status: effectiveStatus,
        start_date: row.start_date ?? undefined,
        paid: row.paid,
        entitlements: (row as any).entitlements,
        effective_entitlements: resolveCreatorEntitlements(
            row.plan_name ?? row.plan?.name ?? (row as any).name,
            (row as any).entitlements,
            (row as any).effective_entitlements,
        ),
        flutterwave_plan_codes: (row as any).flutterwave_plan_codes,
        subscription_details: row.subscription_details as ISubscriptionPlan['subscription_details'],
    } as ISubscriptionPlan;
}

/** Match catalog plan cards to the user's subscription (Stripe price id, Paystack code, or Flutterwave ids). */
export function resolveCreatorSubscriptionPlanCodes(sub?: ISubscriptionPlan | null): Set<string> {
    const codes = new Set<string>();
    if (!sub) return codes;
    const add = (c?: string | number | null) => {
        const s = c != null ? String(c).trim() : '';
        if (s) codes.add(s);
    };
    add(sub.plan_code);
    add(sub.subscription_plan);
    add(sub.plan?.plan_code);
    add(sub.plan?.id != null ? String(sub.plan.id) : null);
    add(sub.subscription_details?.flutterwave_plan_id);
    add(sub.subscription_details?.catalog_plan_code);
    const map = sub.flutterwave_plan_codes;
    if (map && typeof map === 'object') {
        Object.values(map).forEach(add);
    }
    return codes;
}

function normalisePlanInterval(plan?: ISubscriptionPlan | null): string {
    const raw = String(plan?.interval ?? plan?.plan?.interval ?? '')
        .toLowerCase()
        .replace('monthly', 'month')
        .replace('yearly', 'year');
    return raw;
}

function catalogAndSubShareInterval(catalogPlan: ISubscriptionPlan, sub: ISubscriptionPlan): boolean {
    const a = normalisePlanInterval(catalogPlan);
    const b = normalisePlanInterval(sub);
    if (!a || !b) return true;
    return a === b;
}

export function isCurrentCreatorCatalogPlan(catalogPlan: ISubscriptionPlan, sub?: ISubscriptionPlan | null): boolean {
    if (!sub) return false;
    if (!catalogAndSubShareInterval(catalogPlan, sub)) return false;

    const subCodes = resolveCreatorSubscriptionPlanCodes(sub);
    const catalogCodes = resolveCreatorSubscriptionPlanCodes(catalogPlan);
    for (const code of catalogCodes) {
        if (subCodes.has(code)) return true;
    }

    const catalogClass = normalizeCreatorPlanClass(catalogPlan.name ?? catalogPlan.plan_name);
    const subClass = normalizeCreatorPlanClass(sub.plan_name ?? sub.name);
    return Boolean(catalogClass && subClass && catalogClass === subClass);
}

export function enrichCreatorSubscriptionFromCatalog(
    sub: ISubscriptionPlan | undefined,
    catalogPlans: ISubscriptionPlan[] | undefined,
): ISubscriptionPlan | undefined {
    if (!sub || !catalogPlans?.length) return sub;

    const codes = resolveCreatorSubscriptionPlanCodes(sub);
    const matched = catalogPlans.find(p => codes.has(p.plan_code));
    if (!matched) return sub;

    return {
        ...sub,
        description: sub.description || matched.description,
        features: sub.features?.length ? sub.features : matched.features,
        amount: sub.amount && sub.amount > 0 ? sub.amount : matched.amount,
        interval: sub.interval || matched.interval,
        converted_amount: sub.converted_amount || matched.converted_amount,
        formatted_for_display: sub.formatted_for_display || matched.formatted_for_display,
        source: sub.source || inferSource(getCreatorSubscriptionProvider(sub)) || matched.source,
        entitlements: sub.entitlements ?? matched.entitlements,
        effective_entitlements: resolveCreatorEntitlements(
            sub.plan_name ?? sub.name ?? matched.name,
            sub.entitlements ?? matched.entitlements,
            sub.effective_entitlements ?? matched.effective_entitlements,
        ),
        plan: {
            ...matched.plan,
            ...sub.plan,
            id: sub.plan?.id ?? matched.plan?.id ?? matched.plan_code,
            plan_code: sub.plan?.plan_code ?? matched.plan_code,
            amount: sub.plan?.amount && sub.plan.amount > 0 ? sub.plan.amount : matched.amount,
            interval: sub.plan?.interval ?? matched.plan?.interval ?? matched.interval,
        },
    };
}

export function parseUpdateCreatorSubscriptionPaymentResult(
    payload: unknown,
): IUpdateCreatorSubscriptionPaymentResult | null {
    if (!payload || typeof payload !== 'object') return null;
    const root = payload as Record<string, unknown>;
    const data = (root.data ?? root) as Record<string, unknown>;
    const subscriptionId =
        typeof data.subscription_id === 'string'
            ? data.subscription_id
            : data.subscription_id != null
            ? String(data.subscription_id)
            : '';

    if (data.provider === 'paystack' && subscriptionId) {
        if (data.action === 'updated') {
            return {
                provider: 'paystack',
                action: 'updated',
                subscription_id: subscriptionId,
                payment_method_id: typeof data.payment_method_id === 'string' ? data.payment_method_id : undefined,
                subscription_code: typeof data.subscription_code === 'string' ? data.subscription_code : undefined,
            };
        }
        if (data.action === 'redirect' && typeof data.manage_payment_url === 'string') {
            return {
                provider: 'paystack',
                action: 'redirect',
                subscription_id: subscriptionId,
                manage_payment_url: data.manage_payment_url,
            };
        }
    }

    if (data.provider === 'flutterwave' && subscriptionId) {
        if (data.action === 'updated') {
            return {
                provider: 'flutterwave',
                action: 'updated',
                subscription_id: subscriptionId,
                payment_method_id: typeof data.payment_method_id === 'string' ? data.payment_method_id : undefined,
            };
        }
        if (data.action === 'redirect' && typeof data.manage_payment_url === 'string') {
            return {
                provider: 'flutterwave',
                action: 'redirect',
                subscription_id: subscriptionId,
                manage_payment_url: data.manage_payment_url,
            };
        }
    }

    if (
        data.provider === 'stripe' &&
        data.action === 'updated' &&
        subscriptionId &&
        typeof data.payment_method_id === 'string'
    ) {
        return {
            provider: 'stripe',
            action: 'updated',
            subscription_id: subscriptionId,
            stripe_subscription_id: typeof data.stripe_subscription_id === 'string' ? data.stripe_subscription_id : '',
            payment_method_id: data.payment_method_id,
            stripe_status: typeof data.stripe_status === 'string' ? data.stripe_status : null,
        };
    }

    return null;
}

export function canReactivatePaystackCreatorSubscription(
    sub: ICreatorSubscription | ISubscriptionPlan | null | undefined,
): boolean {
    if (!sub?._id) return false;
    if (getCreatorSubscriptionProvider(sub) !== 'paystack') return false;
    const status = sub.status ?? '';
    return status === 'non-renewing' || status === 'attention';
}

export function shouldShowCreatorSubscriptionManage(sub: ISubscriptionPlan | null | undefined): boolean {
    if (!sub) return false;
    const planName = sub.plan_name ?? sub.name ?? '';
    if (!planName || planName === 'Free') return false;
    if (isTerminalCreatorSubscriptionStatus(sub.status)) return false;
    return isManageableCreatorSubscriptionStatus(sub.status) || hasActiveCreatorSubscriptionBenefits(sub);
}

/** Canceled/ended paid plan — show status strip + resubscribe (not Manage). */
export function shouldShowCreatorCanceledSubscriptionStrip(sub: ISubscriptionPlan | null | undefined): boolean {
    if (!sub?._id) return false;
    if (shouldShowCreatorSubscriptionManage(sub)) return false;
    if (!isTerminalCreatorSubscriptionStatus(sub.status)) return false;
    const planName = sub.plan_name ?? sub.name ?? '';
    return Boolean(planName && planName !== 'Free');
}

/** True when user has an active paid plan — used for upgrade vs new subscribe. */
export function hasManageableCreatorSubscription(sub: ISubscriptionPlan | null | undefined): boolean {
    return shouldShowCreatorSubscriptionManage(sub);
}

export function isCreatorCatalogPlanSelected(catalogPlan: ISubscriptionPlan, sub?: ISubscriptionPlan | null): boolean {
    if (!sub || !isCurrentCreatorCatalogPlan(catalogPlan, sub)) return false;
    return isManageableCreatorSubscriptionStatus(sub.status) || hasActiveCreatorSubscriptionBenefits(sub);
}
